import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Users,
  Target,
  Trophy,
  Timer,
  FileText,
  ClipboardCheck,
  Sparkles,
} from "lucide-react";

const MOTIVES = [
  "Wettkampfantrieb",
  "Teamorientierung",
  "Führungsverhalten",
  "Lernmotivation",
  "Disziplin",
  "Belastbarkeit",
  "Kreativität",
  "Mut",
  "Leistungsbereitschaft",
  "Emotionale Stabilität",
  "Fußballintelligenz",
  "Entwicklungsorientierung",
];

const ARCHES = [
  { name: "DER KAPITÄN", tag: "Führt, organisiert, vereint." },
  { name: "DER KÄMPFER", tag: "Wettkampf ist alles." },
  { name: "DER STRATEGE", tag: "Sieht das Spiel drei Züge voraus." },
  { name: "DER MOTOR", tag: "Läuft immer, gibt nie auf." },
  { name: "DER KREATIVSPIELER", tag: "Erfindet das Unerwartete." },
  { name: "DER TEAMPLAYER", tag: "Team vor Ego. Immer." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pitch-lines opacity-40 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1579952363873-27f3bade9f55?auto=format&fit=crop&w=1920&q=80)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.4) contrast(1.1)",
            maskImage:
              "linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.15) 100%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-[#050505]" />

        <div className="relative mx-auto max-w-7xl px-6 pt-24 pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#CCFF00]/30 bg-[#CCFF00]/5 px-4 py-1.5 rounded-sm mb-8">
              <span className="w-2 h-2 bg-[#CCFF00] rounded-full animate-pulse" />
              <span className="font-display text-xs tracking-[0.25em] text-[#CCFF00]">
                Football Motivation Profile / FMP<sup>&reg;</sup>
              </span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6"
            >
              VERSTEHE, WAS
              <br />
              DEINE SPIELER
              <br />
              <span className="text-[#CCFF00]">WIRKLICH ANTREIBT.</span>
            </h1>
            <p className="text-lg text-white/70 mb-10 max-w-xl leading-relaxed">
              Das erste sportartspezifische Motivprofil für den Nachwuchsfußball.
              12 Motive. 60 Situationen aus dem Spielalltag. Ein präzises
              Spielerprofil in 10–12 Minuten.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="hero-cta-demo"
                onClick={() => navigate("/demo")}
                className="btn-primary"
              >
                Test kostenlos starten
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                data-testid="hero-cta-register"
                onClick={() => navigate("/register")}
                className="btn-ghost"
              >
                Als Trainer registrieren
              </button>
            </div>

            <div className="mt-16 flex flex-wrap gap-8 text-sm">
              <Stat label="Motive" value="12" />
              <Stat label="Situationsfragen" value="60" />
              <Stat label="Testdauer" value="10 Min" />
              <Stat label="Spielertypen" value="8" />
            </div>
          </div>
        </div>
      </section>

      {/* Motive */}
      <section className="relative py-24 border-y border-white/5">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 max-w-2xl">
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / DIE 12 FUSSBALL-MOTIVE
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              Nicht was Spieler <span className="text-white/40">können.</span>
              <br />
              Sondern was sie <span className="text-[#CCFF00]">wirklich wollen.</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {MOTIVES.map((m, i) => (
              <div
                key={m}
                data-testid={`landing-motive-${i}`}
                className="border border-white/10 bg-[#111214] p-5 rounded-md hover:border-[#CCFF00]/40 hover:bg-[#CCFF00]/5 transition-all group"
              >
                <div className="font-display text-xs tracking-wider text-white/40 mb-2">
                  0{i + 1 < 10 ? "0" + (i + 1) : i + 1}
                </div>
                <div className="font-heading font-bold text-lg text-white group-hover:text-[#CCFF00] transition-colors">
                  {m}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12">
            <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
              / SO FUNKTIONIERT ES
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
              In 10 Minuten zum Profil.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Step
              icon={<Users className="w-6 h-6" />}
              num="01"
              title="Spieler einladen"
              desc="Team anlegen und für jeden Spieler einen individuellen Testlink erhalten."
            />
            <Step
              icon={<ClipboardCheck className="w-6 h-6" />}
              num="02"
              title="60 Situationen"
              desc="Spieler beantwortet realistische Fußballsituationen – keine 'Stimme zu'-Fragen."
            />
            <Step
              icon={<FileText className="w-6 h-6" />}
              num="03"
              title="Profil & Team-Analyse"
              desc="Trainer erhält sofort das Spielerprofil, PDF-Report und die komplette Team-Analyse."
            />
          </div>
        </div>
      </section>

      {/* Image strip: real football scenes */}
      <section className="relative overflow-hidden border-t border-white/5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
          {[
            "https://images.unsplash.com/photo-1517927033932-b3d18e61fb3a?auto=format&fit=crop&w=600&q=70",
            "https://images.unsplash.com/photo-1518604666860-9ed391f76460?auto=format&fit=crop&w=600&q=70",
            "https://images.unsplash.com/photo-1522778119026-d647f0596c20?auto=format&fit=crop&w=600&q=70",
            "https://images.unsplash.com/photo-1551958219-acbc608c6377?auto=format&fit=crop&w=600&q=70",
          ].map((src, i) => (
            <div
              key={i}
              className="relative aspect-square md:aspect-[4/5] overflow-hidden group"
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 scale-105 group-hover:scale-100"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              {i === 0 && (
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-display text-[10px] tracking-widest text-[#CCFF00] mb-1">
                    / VEREINE
                  </div>
                  <div className="font-heading font-black text-lg text-white leading-tight">
                    Jugendfußball
                  </div>
                </div>
              )}
              {i === 1 && (
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-display text-[10px] tracking-widest text-[#CCFF00] mb-1">
                    / TRAINING
                  </div>
                  <div className="font-heading font-black text-lg text-white leading-tight">
                    Leistungszentren
                  </div>
                </div>
              )}
              {i === 2 && (
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-display text-[10px] tracking-widest text-[#CCFF00] mb-1">
                    / SPIELTAG
                  </div>
                  <div className="font-heading font-black text-lg text-white leading-tight">
                    Wettkampf
                  </div>
                </div>
              )}
              {i === 3 && (
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-display text-[10px] tracking-widest text-[#CCFF00] mb-1">
                    / SCOUTING
                  </div>
                  <div className="font-heading font-black text-lg text-white leading-tight">
                    Talent-Analyse
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Archetypes */}
      <section className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-3">
                / DIE SPIELERTYPEN
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight max-w-2xl">
                Jedes Profil hat Stärken.
                <br />
                Keine richtigen oder falschen Antworten.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ARCHES.map((a, i) => (
              <div
                key={a.name}
                data-testid={`landing-archetype-${i}`}
                className="relative p-8 border border-white/10 bg-gradient-to-br from-[#1a1c20] to-[#050505] rounded-md hover:border-[#CCFF00]/40 transition-colors group overflow-hidden"
              >
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#CCFF00]/10 rounded-full blur-3xl group-hover:bg-[#CCFF00]/20 transition-colors" />
                <div className="relative">
                  <div className="font-display text-xs tracking-widest text-white/30 mb-3">
                    Typ {i + 1}
                  </div>
                  <div className="font-heading font-black text-2xl leading-tight mb-2 group-hover:text-[#CCFF00] transition-colors">
                    {a.name}
                  </div>
                  <div className="text-sm text-white/60 italic">{a.tag}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-white/5 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1551958219-acbc608c6377?auto=format&fit=crop&w=1920&q=80)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.5)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/70 to-[#050505]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="font-display text-xs tracking-[0.3em] text-[#CCFF00] mb-4">
            / FÜR TRAINER, VEREINE UND LEISTUNGSZENTREN
          </div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl leading-tight tracking-tight mb-6">
            Teste deine <span className="text-[#CCFF00]">gesamte Mannschaft</span> in 10 Minuten.
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
            Individuelle Spielerprofile, professionelle PDF-Berichte und eine
            komplette Team-Analyse – wer kann Kapitän werden, wer motiviert,
            wer reagiert empfindlich, wo fehlen Typen im Team.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register" data-testid="cta-register-bottom" className="btn-primary">
              Kostenloses Trainer-Konto
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/demo" data-testid="cta-demo-bottom" className="btn-ghost">
              <Sparkles className="w-5 h-5" />
              Erst Demo-Test machen
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-12">
        <div className="mx-auto max-w-7xl px-6 flex flex-wrap justify-between gap-4 text-sm text-white/40">
          <div>
            FMP · Football Motivation Profile
          </div>
          <div>Entwicklung &amp; Design by Metin Albayrak</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-heading font-black text-3xl text-white">{value}</div>
      <div className="font-display text-xs tracking-widest text-white/40 mt-1">
        {label}
      </div>
    </div>
  );
}

function Step({ num, title, desc, icon }) {
  return (
    <div className="p-8 border border-white/10 bg-[#111214] rounded-md hover:border-[#CCFF00]/30 transition-colors">
      <div className="flex items-start justify-between mb-6">
        <div className="w-12 h-12 border border-[#CCFF00]/40 flex items-center justify-center rounded-sm text-[#CCFF00]">
          {icon}
        </div>
        <div className="font-display font-black text-4xl text-white/10">{num}</div>
      </div>
      <h3 className="font-heading font-bold text-xl mb-2">{title}</h3>
      <p className="text-white/60 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}
